import { useEffect, useState } from "react"
import { Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { fieldErrorTextClass, fieldInputClass, fieldLabelClass, focusRing, formButtonClass, primaryButtonClass } from "../lightTheme"
import PageControls from "../benchmarkApi/PageControls"
import { searchLeads } from "../benchmarkApi/benchmarkClient"
import {
  buildCompanySearchBody,
  companyQueryError,
  countLabel,
  LOADING_VALUE,
  mapLead,
} from "../benchmarkApi/reportData"
import { searchCopy } from "./searchContent"

const cardShadow =
  "shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_2px_2px_0_rgba(0,0,0,0.04),0_8px_16px_-4px_rgba(0,0,0,0.04),0_0_0_1px_#FAFAFA]"

const headingClass =
  "text-balance text-[40px] font-bold leading-[48px] tracking-[-0.04em] text-[#171717] max-md:text-[28px] max-md:leading-8"

const longButtonClass = cn(
  primaryButtonClass,
  "h-auto min-h-10 max-w-full whitespace-normal px-4 py-2 text-left"
)

export function AuditCard({ company }) {
  const facts = [
    `Measured First Response Time: ${company.responseLabel}`,
    `Status: ${company.statusLabel}`,
  ]

  return (
    <article className={`flex w-full flex-col gap-6 rounded-xl bg-white p-6 ${cardShadow}`}>
      <div className="flex flex-col gap-2">
        <h3 className="text-[24px] font-bold leading-8 tracking-[-0.04em] text-[#171717]">
          {`${company.name} — ${company.location}`}
        </h3>
        {company.website ? (
          <p className="break-all text-[14px] font-normal leading-5 text-[#4D4D4D]">
            {company.website}
          </p>
        ) : null}
      </div>
      <ul className="m-0 flex list-disc flex-col gap-3 pl-5 text-[16px] font-normal leading-6 text-[#4D4D4D]">
        {facts.map((fact) => (
          <li key={fact}>{fact}</li>
        ))}
      </ul>
      <button type="button" className={longButtonClass}>
        {searchCopy.foundCta}
      </button>
    </article>
  )
}

function MissingCard({ domain, count }) {
  return (
    <form
      className={`flex w-full flex-col gap-6 rounded-xl bg-white p-6 ${cardShadow}`}
      onSubmit={(event) => event.preventDefault()}
    >
      <div className="flex flex-col gap-3">
        <h3 className="text-balance text-[24px] font-bold leading-8 tracking-[-0.04em] text-[#171717]">
          {searchCopy.missingTitle.replaceAll("{count}", count)}
        </h3>
        <p className="text-[16px] font-normal leading-6 text-[#4D4D4D]">
          {searchCopy.missingBody}
        </p>
      </div>
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-2" htmlFor="benchmark-domain">
          <span className={fieldLabelClass}>{searchCopy.domainLabel}</span>
          <input
            id="benchmark-domain"
            type="text"
            name="domain"
            defaultValue={domain}
            placeholder={searchCopy.domainPlaceholder}
            className={`${fieldInputClass} w-full`}
          />
        </label>
        <label className="flex flex-col gap-2" htmlFor="benchmark-phone">
          <span className={fieldLabelClass}>{searchCopy.phoneLabel}</span>
          <input
            id="benchmark-phone"
            type="tel"
            name="phone"
            placeholder={searchCopy.phonePlaceholder}
            className={`${fieldInputClass} w-full`}
          />
        </label>
      </div>
      <button type="submit" className={longButtonClass}>
        {searchCopy.missingCta}
      </button>
    </form>
  )
}

export default function BenchmarkSearchSection({ statsStatus, totalCompanies }) {
  const [query, setQuery] = useState("")
  const [submitted, setSubmitted] = useState("")
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState(null)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState("idle")
  const [error, setError] = useState("")
  const count = countLabel(statsStatus, totalCompanies)

  useEffect(() => {
    if (!submitted) return undefined
    const controller = new AbortController()
    let cancelled = false
    setStatus("loading")
    setError("")
    setRows([])
    setTotal(0)

    searchLeads(buildCompanySearchBody(submitted, page), controller.signal)
      .then((payload) => {
        if (cancelled) return
        const items = Array.isArray(payload?.items) ? payload.items.map(mapLead) : []
        setRows(items)
        setTotal(typeof payload?.total === "number" ? payload.total : 0)
        setStatus("ready")
      })
      .catch((requestError) => {
        if (cancelled || requestError?.name === "AbortError") return
        setStatus("error")
        setError(requestError?.message || "The audit data could not be loaded.")
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [submitted, page])

  const selected = rows.find((row) => row.id === selectedId) ?? null
  const domainPrefill = submitted.includes(".") && !submitted.includes(" ") ? submitted.trim() : ""

  function onSubmit(event) {
    event.preventDefault()
    const next = query.trim()
    setSelectedId(null)
    setPage(1)
    const tooLong = companyQueryError(next)
    if (!next) {
      setSubmitted("")
      setRows([])
      setTotal(0)
      setStatus("idle")
      setError("")
      return
    }
    if (tooLong) {
      setSubmitted("")
      setRows([])
      setTotal(0)
      setStatus("error")
      setError(tooLong)
      return
    }
    setSubmitted(next)
  }

  return (
    <section className="flex w-full translate-x-[calc(50vw-50%-1.5rem)] flex-col gap-8">
      <h2 className={headingClass}>{searchCopy.title.replaceAll("{count}", count)}</h2>
      <form className="flex w-full flex-col items-start gap-3 sm:flex-row sm:items-center" onSubmit={onSubmit}>
        <input
          type="search"
          value={query}
          placeholder={searchCopy.placeholder}
          aria-label="Company name"
          onChange={(event) => setQuery(event.target.value)}
          className={`${fieldInputClass} w-full min-w-0 flex-1`}
        />
        <button type="submit" className={`${formButtonClass} shrink-0 gap-1.5`}>
          <Search className="size-3.5" strokeWidth={1.5} aria-hidden="true" />
          {searchCopy.button}
        </button>
      </form>
      {status === "loading" ? <p className="text-[14px] font-normal leading-5 text-[#4D4D4D]">{LOADING_VALUE}</p> : null}
      {status === "error" ? <p className={fieldErrorTextClass}>{error}</p> : null}
      {status === "ready" && total === 0 ? <MissingCard key={submitted} domain={domainPrefill} count={count} /> : null}
      {selected ? <AuditCard company={selected} /> : null}
      {rows.length > 0 && !selected ? (
        <>
          <ul className={`m-0 flex w-full list-none flex-col overflow-hidden rounded-xl bg-white p-0 ${cardShadow}`}>
            {rows.map((company) => (
              <li
                key={company.id}
                className="border-b border-solid border-black/[0.08] last:border-b-0"
              >
                <button
                  type="button"
                  onClick={() => setSelectedId(company.id)}
                  className={`flex w-full cursor-pointer items-center justify-between gap-4 bg-white px-6 py-4 text-left transition-colors duration-100 hover:bg-[#FAFAFA] ${focusRing}`}
                >
                  <span className="text-[16px] font-medium leading-6 text-[#171717]">
                    {company.name}
                  </span>
                  <span className="shrink-0 text-[14px] font-normal leading-5 text-[#4D4D4D]">
                    {company.location}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <PageControls
            page={page}
            total={total}
            onPage={(nextPage) => {
              setSelectedId(null)
              setPage(nextPage)
            }}
          />
        </>
      ) : null}
    </section>
  )
}
